import React, { useState, useEffect, useCallback } from "react";
import LandingPage from "./components/LandingPage";
import AuthScreen from "./components/AuthScreen";
import Dashboard from "./components/Dashboard";
import SocialOAuthCallback from "./components/SocialOAuthCallback";
import GoogleOAuthCallback from "./components/GoogleOAuthCallback";
import { TermsPage, PrivacyPage } from "./components/TermsAndPrivacy";
import { getStoredTokens, clearTokens, setGlobalAuthErrorHandler, clearGlobalAuthErrorHandler } from "./api/client";
import { logout as authLogout } from "./api/auth";
import { fetchUserSubscription } from "./api/billing";
import { AppLanguageProvider, useAppLanguage } from "./context/AppLanguageContext";

const SESSION_KEY = 'claw9_session';
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes

// Wrapper components for legal pages that need language context
const TermsPageWrapper = () => {
  const { language } = useAppLanguage();
  const handleBack = () => {
    window.location.href = '/';
  };
  return <TermsPage language={language} onBack={handleBack} />;
};

const PrivacyPageWrapper = () => {
  const { language } = useAppLanguage();
  const handleBack = () => {
    window.location.href = '/';
  };
  return <PrivacyPage language={language} onBack={handleBack} />;
};

const App = () => {
  const [currentView, setCurrentView] = useState('landing'); // 'landing', 'auth', 'dashboard'
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedToken = getStoredTokens();
        const storedSession = localStorage.getItem(SESSION_KEY);

        if (storedToken && storedSession) {
          const sessionData = JSON.parse(storedSession);

          // Create initial user data from session
          const userData = {
            email: sessionData.email,
            name: sessionData.name || sessionData.email?.split('@')[0],
            subscription: sessionData.subscription || 'basic',
            token: storedToken
          };

          // Try to fetch fresh subscription data from API
          try {
            const subscriptionResponse = await fetchUserSubscription({ skipGlobalAuthHandler: true });
            if (subscriptionResponse?.data) {
              const subData = subscriptionResponse.data;
              userData.subscription = subData.tier || subData.plan || userData.subscription;
              userData.subscriptionData = subData;
              console.log('[App] Refreshed subscription:', userData.subscription);

              // Update stored session with fresh subscription
              localStorage.setItem(SESSION_KEY, JSON.stringify({
                email: userData.email,
                name: userData.name,
                subscription: userData.subscription
              }));
            }
          } catch (subError) {
            console.warn('[App] Could not refresh subscription, using cached:', subError.message);
          }

          setUser(userData);
          setCurrentView('dashboard');
        }
      } catch (e) {
        console.error('[App] Error checking auth:', e);
        clearTokens();
        localStorage.removeItem(SESSION_KEY);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    // Set up global auth error handler for automatic logout on auth failure
    setGlobalAuthErrorHandler((error) => {
      console.error('[App] Global auth error - logging out:', error?.message);
      handleLogout();
    });

    checkAuth();

    // Cleanup on unmount
    return () => {
      clearGlobalAuthErrorHandler();
    };
  }, []);

  // Activity tracking for session timeout
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  useEffect(() => {
    if (currentView !== 'dashboard') return;

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Check for inactivity
    const inactivityCheck = setInterval(() => {
      if (Date.now() - lastActivity > INACTIVITY_TIMEOUT) {
        console.log('[App] Session timeout due to inactivity');
        handleLogout();
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(inactivityCheck);
    };
  }, [currentView, lastActivity, updateActivity]);

  const handleLoginClick = () => {
    setAuthMode('login');
    setCurrentView('auth');
  };

  const handleSignupClick = () => {
    setAuthMode('signup');
    setCurrentView('auth');
  };

  const handleLoginSuccess = async (data) => {
    // Extract user data from various response formats
    const userData = {
      email: data?.email || data?.user?.email || data?.data?.user?.email,
      name: data?.name || data?.user?.name || data?.data?.user?.name,
      token: data?.token || data?.tokens || data?.data?.token
    };

    // Fetch real subscription data from API
    try {
      const subscriptionResponse = await fetchUserSubscription();
      if (subscriptionResponse?.data) {
        // Map the subscription response to our expected format
        const subData = subscriptionResponse.data;
        userData.subscription = subData.tier || subData.plan || 'basic';
        userData.subscriptionData = subData; // Store full subscription data
        console.log('[App] Subscription fetched:', userData.subscription, subData);
      }
    } catch (subscriptionError) {
      console.warn('[App] Could not fetch subscription, defaulting to basic:', subscriptionError.message);
      userData.subscription = 'basic';
    }

    // Store session for persistence
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        email: userData.email,
        name: userData.name,
        subscription: userData.subscription
      }));
    } catch (e) {
      console.warn('[App] Could not persist session:', e);
    }

    setUser(userData);
    setLastActivity(Date.now());
    setCurrentView('dashboard');
  };

  const handleLogout = useCallback(() => {
    // Clear auth state (clears tokens from storage)
    authLogout();

    // Clear session storage
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch (e) {
      console.warn('[App] Could not clear session:', e);
    }

    // Clear local state
    setUser(null);
    setCurrentView('landing');
    setLastActivity(Date.now());
  }, []);

  // Handle special routes first (before auth check)
  if (typeof window !== "undefined") {
    const pathname = window.location.pathname;

    if (pathname.startsWith("/integrations/social/")) {
      return <SocialOAuthCallback />;
    }

    if (pathname.startsWith("/integrations/google/callback")) {
      return <GoogleOAuthCallback />;
    }

    if (pathname === "/terms" || pathname === "/terms/") {
      return (
        <AppLanguageProvider>
          <TermsPageWrapper />
        </AppLanguageProvider>
      );
    }

    if (pathname === "/privacy" || pathname === "/privacy/") {
      return (
        <AppLanguageProvider>
          <PrivacyPageWrapper />
        </AppLanguageProvider>
      );
    }
  }

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#ff4d4d] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLanguageProvider>
      <div className="min-h-screen bg-[#050810] text-white">
        {currentView === 'landing' && (
          <LandingPage
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
          />
        )}

        {currentView === 'auth' && (
          <AuthScreen
            initialMode={authMode}
            onClose={() => setCurrentView('landing')}
            onLoginSuccess={handleLoginSuccess}
          />
        )}

        {currentView === 'dashboard' && (
          <Dashboard
            user={user}
            onLogout={handleLogout}
          />
        )}
      </div>
    </AppLanguageProvider>
  );
};

export default App;
